/**
 * One voice channel in the sidebar — Discord's model exactly: a room that is
 * always there, that you step into and out of, with the people already inside
 * listed underneath it before you commit to joining. Whoever is sharing a
 * screen carries a LIVE badge, and clicking them takes you straight to that
 * screen on the channel page.
 *
 * The roster comes from the 10s presence poll, except for the channel you are
 * actually in: there the live `Room` is both fresher and authoritative, so your
 * own join shows instantly instead of up to ten seconds later, and mute state
 * (which presence does not carry) becomes visible.
 */
import { Loader2, MicOff, Settings, Volume2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { useVoiceRoom } from '../../contexts/VoiceRoomContext';
import type { VoiceChannel, VoiceParticipant } from '../../lib/voiceApi';
import { useAuthStore } from '../../store/useAuthStore';
import { useTestMode } from '../testMode/TestModeContext';
import { Avatar } from '../tasks/linear/Avatar';

/** The channel page, optionally opened straight onto one person's screen. */
export function voiceChannelPath(channelId: string, watchUserId?: string): string {
  const base = `/team-chat/voice/${encodeURIComponent(channelId)}`;
  return watchUserId ? `${base}?watch=${encodeURIComponent(watchUserId)}` : base;
}

/** What both the presence poll and the live room can tell us about a person. */
export interface RosterEntry {
  userId: string;
  name: string;
  /** Null when unknown — presence does not report it, only the live room does. */
  muted: boolean | null;
  sharingScreen: boolean;
  isLocal: boolean;
}

export function LiveBadge({ className = '' }: { className?: string }) {
  const { t } = useI18n();
  return (
    <span
      className={`shrink-0 rounded bg-red-500 px-1 text-[9px] font-bold leading-4 tracking-wide text-white ${className}`}
    >
      {t('teamChat.voiceLiveBadge')}
    </span>
  );
}

export function VoiceChannelRow({
  channel,
  participants,
  canManage,
  onSettings,
  onNavigate,
}: {
  channel: VoiceChannel;
  participants: VoiceParticipant[];
  canManage: boolean;
  onSettings: () => void;
  onNavigate?: () => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const me = useAuthStore((s) => s.user);
  const { enabled: testMode } = useTestMode();
  const { channelId: activeId, status, members, speakingIds, join } = useVoiceRoom();

  const isHere = activeId === channel.id;
  const connecting = isHere && status === 'connecting';
  const selected = pathname === voiceChannelPath(channel.id);
  const speaking = new Set(isHere ? speakingIds : []);

  const roster: RosterEntry[] = isHere
    ? members
    : participants.map((p) => ({
        ...p,
        muted: null,
        isLocal: p.userId === me?.id,
      }));

  // Joining and opening the page are one gesture, like clicking a Discord
  // voice channel. Test Mode must never open a microphone or a real WebRTC
  // connection — the demo shows a populated channel, it doesn't dial into one —
  // so there the click only opens the page.
  const enter = (watchUserId?: string) => {
    if (!testMode && !isHere) void join(channel.id, channel.name);
    navigate(voiceChannelPath(channel.id, watchUserId));
    onNavigate?.();
  };

  return (
    <div>
      <div className="group relative">
        <button
          type="button"
          onClick={() => enter()}
          disabled={connecting}
          title={testMode ? t('teamChat.voiceTestMode') : t('teamChat.voiceJoin')}
          className={[
            'flex w-full items-center gap-2.5 rounded-md py-2 pl-3 text-left text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
            canManage ? 'pr-8' : 'pr-3',
            selected || isHere
              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
              : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          ].join(' ')}
        >
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sidebar-accent">
            {connecting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Volume2
                size={14}
                className={isHere ? 'text-emerald-500' : 'text-muted-foreground'}
              />
            )}
          </span>
          <span className="min-w-0 flex-1 truncate">{channel.name}</span>
        </button>
        {/* The gear only surfaces on hover, exactly like Discord's — a row of
            permanent gears would make the list read as a settings page. It
            stays reachable by keyboard through focus. */}
        {canManage && (
          <button
            type="button"
            onClick={onSettings}
            aria-label={t('teamChat.voiceChannelSettings')}
            title={t('teamChat.voiceChannelSettings')}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-sidebar-accent hover:text-foreground focus:opacity-100 group-hover:opacity-100 touch:opacity-100"
          >
            <Settings size={14} />
          </button>
        )}
      </div>

      {roster.length > 0 && (
        <ul className="mt-0.5 space-y-0.5 pl-6">
          {roster.map((p) => (
            <li key={p.userId}>
              <button
                type="button"
                onClick={() => enter(p.sharingScreen ? p.userId : undefined)}
                title={
                  p.sharingScreen
                    ? t('teamChat.voiceWatchOf', { name: p.name })
                    : p.isLocal
                      ? t('teamChat.voiceYou')
                      : p.name
                }
                className="flex w-full items-center gap-2 rounded-md py-1 pl-3 pr-2 text-left text-xs text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              >
                <span
                  className={[
                    'shrink-0 rounded-full ring-2 transition-colors',
                    speaking.has(p.userId) ? 'ring-emerald-500' : 'ring-transparent',
                  ].join(' ')}
                >
                  <Avatar name={p.name} size={18} />
                </span>
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                {p.muted && (
                  <span title={t('teamChat.voiceMuted')} className="shrink-0 text-muted-foreground">
                    <MicOff size={12} />
                  </span>
                )}
                {p.sharingScreen && <LiveBadge />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
